import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import Grid from '@mui/material/Grid';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  TextField
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import FormHelperText from '@mui/material/FormHelperText';
import {
  readTerminologyAvailability,
  terminologyAvailabilityFromError
} from 'src/util/terminologyAvailability';
import { AppContext, SnackContext, UserContext } from 'src/store/ContextStore';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import SelectInput from 'src/components/UI/input/SelectInput';
import TextInput from 'src/components/UI/input/TextInput';
import DateInput from 'src/components/UI/input/DateInput';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import { calculateAgeOnDate } from 'src/util/util';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import {
  getClinicalDefaults,
  saveClinicalDefault,
  deleteClinicalDefault
} from './formService';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';
import {
  ChangedField,
  ChangedSectionMarker,
  useChangedFields
} from 'src/components/UI/input/changed-field';

// The fields an edit compares (TGCLOUD-1902), in screen order.
const TISSUE_PATHOLOGY_FIELDS = [
  'tissueSite',
  'morphology',
  'numberOfTumours',
  'sideAffected',
  'sizeMm',
  'tumourStage',
  'nodeStage',
  'metastasisStage',
  'tumourGrade',
  'overallStage',
  'pathologyDoctor',
  'pathLab',
  'labReference',
  'pathReportDate',
  'ageAtRecord',
  'reportRequestDate',
  'reportReceivedDate',
  'blocksRequestedDate',
  'blocksReceivedDate',
  'blocksReturnedDate',
  'tissuePathComment',
  'staffMember'
];

const SIDE_OPTIONS = [
  { label: 'Both', value: 'both' },
  { label: 'Left', value: 'left' },
  { label: 'Not applicable', value: 'not applicable' },
  { label: 'Right', value: 'right' },
  { label: 'Unknown', value: 'unknown' }
];

const TUMOUR_STAGE = ['T0', 'T1', 'T2', 'T3', 'T4', 'Ta', 'Tis', 'Tx'].map(
  (value) => ({ label: value, value })
);

const NODE_STAGE = ['N0', 'N1', 'N2', 'N3', 'Nx'].map((value) => ({
  label: value,
  value
}));

const METASTASIS_STAGE = ['M0', 'M1', 'Mx'].map((value) => ({
  label: value,
  value
}));

const TUMOUR_GRADE = ['G1', 'G2', 'G3', 'G4', 'Gx'].map((value) => ({
  label: value,
  value
}));

const OVERALL_STAGE = ['0', 'I', 'II', 'III', 'IV'].map((value) => ({
  label: value,
  value
}));

// Both pickers search the terminology service (ICD-10-CM and ICD-O), not
// BioPortal.
const TERMINOLOGY_MIN_QUERY_LENGTH = 2;
const TERMINOLOGY_DEBOUNCE_MS = 300;

const getOptionLabel = (option) => {
  if (!option) return '';
  if (typeof option === 'string') return option;
  return option.label || option.value || '';
};

const mergeOptions = (options = [], selectedValue = '') => {
  const merged = [];
  const seen = new Set();
  const add = (option) => {
    const label = getOptionLabel(option);
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(typeof option === 'string' ? { label } : option);
  };

  if (selectedValue) add({ label: selectedValue });
  options.forEach(add);
  return merged;
};

const isOptionEqualToValue = (option, value) =>
  getOptionLabel(option) === getOptionLabel(value);

// What a picker shows for a lookup that did not run (TGCLOUD-1845): the
// system switched off in Settings, or the terminology service unreachable.
// Neither may read as "No matches", which is a claim about the vocabulary.
const lookupNoOptionsText = (unavailable, search) => {
  if (unavailable)
    return translate('Lookup unavailable — this is not a result');
  return search.trim().length >= TERMINOLOGY_MIN_QUERY_LENGTH
    ? translate('No matches')
    : translate('Type to search');
};

const LookupUnavailableHint = ({ unavailable }) =>
  unavailable ? (
    <FormHelperText role="status" sx={{ mx: 0, color: 'warning.dark' }}>
      {unavailable.message}
    </FormHelperText>
  ) : null;

const searchResultsOnly = (options, { inputValue }) => {
  if (!inputValue || inputValue.trim().length < TERMINOLOGY_MIN_QUERY_LENGTH)
    return [];
  return options;
};

export default function TissuePathologyForm({
  data,
  handleClose,
  familyMemberId,
  isEditing,
  model,
  staffMembers,
  staffMemberOptions,
  // How many fields hold an unsaved change: what closing it asks by.
  onChangeCount
}) {
  const t = useAutoTranslation();

  const { content } = useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { user: loggedInUser } = useContext(UserContext);
  const { setSnack } = useContext(SnackContext);

  const patientDob = data?.dob;
  // The age on the calendar day of the pathology report date
  // (calculateAgeOnDate). The date is handed over as stored — a picker instant,
  // or UTC midnight from the spreadsheet importer — and only the raw value says
  // which, so it is read by toLocalDateFromPickerOrImport. `calculateAge`
  // counted elapsed time from a UTC-midnight birth date: a year short on the
  // birthday at any positive offset, and a positive age for a date before the
  // birth, which is now blank. The date field shows the day it read
  // (asPickerDay), so the day on screen is the day the age is from.
  const asPickerDay = toLocalDateFromPickerOrImport;
  const computeAgeAtRecord = useCallback(
    (date) => calculateAgeOnDate(patientDob, date),
    [patientDob]
  );

  const allStaffMembers = useMemo(() => {
    return staffMemberOptions?.map((item) => item.value) || [];
  }, [staffMemberOptions]);

  const staffMembersMap = useMemo(() => {
    return staffMemberOptions?.reduce((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});
  }, [staffMemberOptions]);

  const defaultValues = isEditing
    ? { ...model, ageAtRecord: model.ageAtRecord ?? '' }
    : {
        familyId: user.familyId,
        familyMemberId,
        tissueSite: '',
        morphology: '',
        numberOfTumours: '',
        sideAffected: '',
        sizeMm: '',
        tumourStage: '',
        nodeStage: '',
        metastasisStage: '',
        tumourGrade: '',
        overallStage: '',
        pathologyDoctor: '',
        pathLab: '',
        labReference: '',
        pathReportDate: null,
        ageAtRecord: '',
        reportRequestDate: null,
        reportReceivedDate: null,
        blocksRequestedDate: null,
        blocksReceivedDate: null,
        blocksReturnedDate: null,
        tissuePathComment: '',
        staffMember: loggedInUser?._id ? [loggedInUser._id] : []
      };

  const [formValues, setFormValues] = useState(defaultValues);
  const ageIsComputed = !!patientDob && !!formValues.pathReportDate;
  // Unsaved changes (TGCLOUD-1902): each field against the record as opened.
  const changes = useChangedFields({
    baseline: defaultValues,
    values: formValues,
    mode: isEditing ? 'edit' : 'create',
    fields: TISSUE_PATHOLOGY_FIELDS,
    exclude: (name) => name === 'ageAtRecord' && ageIsComputed
  });
  useEffect(() => {
    onChangeCount?.(changes.unsavedCount);
  }, [changes.unsavedCount]); // eslint-disable-line react-hooks/exhaustive-deps
  // A collapsed request section says how many of its dates are changed.
  const changedIn = (names) =>
    names.filter((name) => changes.isFieldChanged(name)).length;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showReportDates, setShowReportDates] = useState(
    !!(defaultValues.reportRequestDate || defaultValues.reportReceivedDate)
  );
  const [showBlockDates, setShowBlockDates] = useState(
    !!(
      defaultValues.blocksRequestedDate ||
      defaultValues.blocksReceivedDate ||
      defaultValues.blocksReturnedDate
    )
  );
  // TGCLOUD-1788 — the Path Lab picker was `options={[]}`, a literal empty array
  // in the JSX, so it offered nothing and kept nothing. It shares the
  // `laboratories` list with the genetic Test record rather than starting a
  // second one: a site types its labs in once.
  //
  // Tissue site and morphology below are deliberately NOT clinical defaults —
  // they are coded terminology lookups (see the axios calls), and turning them
  // into a free-text list anyone can extend would let uncoded values into a
  // field that is meant to carry coded concepts: ICD-10-CM C-codes for the
  // tissue site, ICD-O-3 morphology codes for the morphology.
  const [labOptions, setLabOptions] = useState([]);

  useEffect(() => {
    getClinicalDefaults('laboratories').then(setLabOptions);
  }, []);

  const [tissueSiteOptions, setTissueSiteOptions] = useState([]);
  const [morphologyOptions, setMorphologyOptions] = useState([]);
  const [tissueSiteSearch, setTissueSiteSearch] = useState('');
  const [morphologySearch, setMorphologySearch] = useState('');
  const [tissueSiteLoading, setTissueSiteLoading] = useState(false);
  const [morphologyLoading, setMorphologyLoading] = useState(false);
  const [tissueSiteUnavailable, setTissueSiteUnavailable] = useState(null);
  const [morphologyUnavailable, setMorphologyUnavailable] = useState(null);

  const tissueSiteOptionList = useMemo(
    () => mergeOptions(tissueSiteOptions, formValues.tissueSite),
    [tissueSiteOptions, formValues.tissueSite]
  );
  const morphologyOptionList = useMemo(
    () => mergeOptions(morphologyOptions, formValues.morphology),
    [morphologyOptions, formValues.morphology]
  );

  useEffect(() => {
    const query = tissueSiteSearch.trim();
    if (query.length < TERMINOLOGY_MIN_QUERY_LENGTH) {
      setTissueSiteOptions([]);
      setTissueSiteLoading(false);
      setTissueSiteUnavailable(null);
      return;
    }

    let active = true;
    const timeout = setTimeout(() => {
      setTissueSiteLoading(true);
      axios
        .get(APICONSTANTS.tissuePathologySearch, {
          params: {
            q: query,
            type: 'tissueSite'
          }
        })
        .then((res) => {
          if (!active) return;
          setTissueSiteUnavailable(readTerminologyAvailability(res.data));
          setTissueSiteOptions(
            Array.isArray(res.data?.results) ? res.data.results : []
          );
        })
        .catch(() => {
          if (!active) return;
          setTissueSiteOptions([]);
          setTissueSiteUnavailable(terminologyAvailabilityFromError());
        })
        .finally(() => {
          if (active) setTissueSiteLoading(false);
        });
    }, TERMINOLOGY_DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [tissueSiteSearch]);

  useEffect(() => {
    const query = morphologySearch.trim();
    if (query.length < TERMINOLOGY_MIN_QUERY_LENGTH) {
      setMorphologyOptions([]);
      setMorphologyLoading(false);
      setMorphologyUnavailable(null);
      return;
    }

    let active = true;
    const timeout = setTimeout(() => {
      setMorphologyLoading(true);
      axios
        .get(APICONSTANTS.tissuePathologySearch, {
          params: {
            q: query,
            type: 'morphology'
          }
        })
        .then((res) => {
          if (!active) return;
          setMorphologyUnavailable(readTerminologyAvailability(res.data));
          setMorphologyOptions(
            Array.isArray(res.data?.results) ? res.data.results : []
          );
        })
        .catch(() => {
          if (!active) return;
          setMorphologyOptions([]);
          setMorphologyUnavailable(terminologyAvailabilityFromError());
        })
        .finally(() => {
          if (active) setMorphologyLoading(false);
        });
    }, TERMINOLOGY_DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [morphologySearch]);

  useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAgeAtRecord(formValues.pathReportDate);
    setFormValues((prev) => {
      const currentAge = prev.ageAtRecord === undefined ? '' : prev.ageAtRecord;
      if (nextAge === '' && currentAge === '') return prev;
      if (nextAge === currentAge) return prev;
      return { ...prev, ageAtRecord: nextAge };
    });
  }, [patientDob, computeAgeAtRecord, formValues.pathReportDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === 'numberOfTumours' || name === 'sizeMm') {
      nextValue = value === '' ? '' : Number(value);
      if (Number.isNaN(nextValue)) return;
    }

    if (name === 'pathReportDate') {
      setFormValues((prevValues) => ({
        ...prevValues,
        ageAtRecord: computeAgeAtRecord(value)
      }));
    }

    setFormValues((prev) => ({
      ...prev,
      [name]: nextValue
    }));
  };

  const buildStaffPayload = (ids = []) =>
    ids
      .map((staffId) =>
        staffMembers?.find((member) => member.userId === staffId)
      )
      .filter(Boolean);

  const deleteNote = async () => {
    if (!model?._id) return;
    setLoading(true);
    axios
      .delete(`/clinical-notes/note`, {
        params: {
          _id: model._id
        }
      })
      .then(() => {
        setSnack({
          status: true,
          msg: t('Record deleted'),
          severity: 'success'
        });
        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('Delete failed! {{err}}', { err }),
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
        setOpen(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const pathologyDoctor = staffMembers?.find(
      (member) => member.userId === formValues.pathologyDoctor
    );

    const payload = {
      ...formValues,
      staffMember: buildStaffPayload(formValues.staffMember),
      pathologyDoctor: pathologyDoctor ? [pathologyDoctor] : [],
      numberOfTumours:
        formValues.numberOfTumours === '' ? null : formValues.numberOfTumours,
      sizeMm: formValues.sizeMm === '' ? null : formValues.sizeMm
    };

    return axios
      .put(APICONSTANTS.tissuePathology, payload)
      .then((res) => {
        handleClose({ exit: true });
        if (res.status === 200) {
          setSnack({
            status: true,
            msg: t('Successfully saved'),
            severity: 'success'
          });
        } else {
          setSnack({
            status: true,
            msg: t('Upload failed!'),
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('Upload failed! {{err}}', { err }),
          severity: 'error'
        });
      });
  };

  return (
    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={() => setOpen(false)}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Tissue Pathology record?"
        content={t(
          'Warning: This action will permanently delete this Tissue Pathology record.'
        )}
      />
      <Grid container spacing={2}>
        <ChangedField
          component={Grid}
          id="modaltop"
          size={6}
          name="tissueSite"
          changed={changes.changed.tissueSite}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Tissue Site</FormLabel>
          </Grid>
          <Box sx={{ position: 'relative' }}>
            <TextInput
              label=""
              variant="outlined"
              fullWidth
              type="autocomplete"
              name="tissueSite"
              value={formValues.tissueSite}
              onChange={(val) =>
                handleChange({
                  target: { name: 'tissueSite', value: val?.label || val || '' }
                })
              }
              onInputChange={(event, newValue, reason) => {
                if (!reason || reason === 'input' || reason === 'clear') {
                  setTissueSiteSearch(newValue || '');
                }
              }}
              options={tissueSiteOptionList}
              filterOptions={searchResultsOnly}
              loading={tissueSiteLoading}
              loadingText={t('Searching...')}
              noOptionsText={lookupNoOptionsText(
                tissueSiteUnavailable,
                tissueSiteSearch
              )}
              isOptionEqualToValue={isOptionEqualToValue}
              // ICD-10-CM C-codes (clinical-note.js), not ICD-O: the ICD-O
              // data holds morphology only (TGCLOUD-1846).
              placeholder={t('Search ICD-10-CM (C codes)')}
              restrictCreate
            />
            <LookupUnavailableHint unavailable={tissueSiteUnavailable} />
          </Box>
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="morphology"
          changed={changes.changed.morphology}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Morphology</FormLabel>
          </Grid>
          <TextInput
            label=""
            variant="outlined"
            fullWidth
            type="autocomplete"
            name="morphology"
            value={formValues.morphology}
            onChange={(val) =>
              handleChange({
                target: { name: 'morphology', value: val?.label || val || '' }
              })
            }
            onInputChange={(event, newValue, reason) => {
              if (!reason || reason === 'input' || reason === 'clear') {
                setMorphologySearch(newValue || '');
              }
            }}
            options={morphologyOptionList}
            filterOptions={searchResultsOnly}
            loading={morphologyLoading}
            loadingText={t('Searching...')}
            noOptionsText={lookupNoOptionsText(
              morphologyUnavailable,
              morphologySearch
            )}
            isOptionEqualToValue={isOptionEqualToValue}
            placeholder={t('Search ICD-O morphology')}
            restrictCreate
          />
          <LookupUnavailableHint unavailable={morphologyUnavailable} />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="numberOfTumours"
          changed={changes.changed.numberOfTumours}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Number of Tumours</FormLabel>
          </Grid>
          <TextField
            variant="outlined"
            fullWidth
            name="numberOfTumours"
            type="number"
            onChange={handleChange}
            value={formValues.numberOfTumours}
            slotProps={{
              htmlInput: { min: 0 }
            }}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="sideAffected"
          changed={changes.changed.sideAffected}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Side Affected</FormLabel>
          </Grid>
          <SelectInput
            fullWidth
            name="sideAffected"
            value={formValues.sideAffected}
            options={SIDE_OPTIONS}
            onChange={handleChange}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="sizeMm"
          changed={changes.changed.sizeMm}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Size (mm)</FormLabel>
          </Grid>
          <TextField
            variant="outlined"
            fullWidth
            name="sizeMm"
            type="number"
            onChange={handleChange}
            value={formValues.sizeMm}
            slotProps={{
              htmlInput: { min: 0 }
            }}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="tumourStage"
          changed={changes.changed.tumourStage}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Tumour Stage</FormLabel>
          </Grid>
          <SelectInput
            fullWidth
            name="tumourStage"
            value={formValues.tumourStage}
            options={TUMOUR_STAGE}
            onChange={handleChange}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="nodeStage"
          changed={changes.changed.nodeStage}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Node Stage</FormLabel>
          </Grid>
          <SelectInput
            fullWidth
            name="nodeStage"
            value={formValues.nodeStage}
            options={NODE_STAGE}
            onChange={handleChange}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="metastasisStage"
          changed={changes.changed.metastasisStage}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Metastasis Stage</FormLabel>
          </Grid>
          <SelectInput
            fullWidth
            name="metastasisStage"
            value={formValues.metastasisStage}
            options={METASTASIS_STAGE}
            onChange={handleChange}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="tumourGrade"
          changed={changes.changed.tumourGrade}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Tumour Grade</FormLabel>
          </Grid>
          <SelectInput
            fullWidth
            name="tumourGrade"
            value={formValues.tumourGrade}
            options={TUMOUR_GRADE}
            onChange={handleChange}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="overallStage"
          changed={changes.changed.overallStage}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Overall Stage</FormLabel>
          </Grid>
          <SelectInput
            fullWidth
            name="overallStage"
            value={formValues.overallStage}
            options={OVERALL_STAGE}
            onChange={handleChange}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="pathologyDoctor"
          changed={changes.changed.pathologyDoctor}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Pathology Doctor</FormLabel>
          </Grid>
          <SelectInput
            fullWidth
            name="pathologyDoctor"
            value={formValues.pathologyDoctor || ''}
            translateOptions={false}
            options={staffMemberOptions}
            onChange={handleChange}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="pathLab"
          changed={changes.changed.pathLab}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Path Lab</FormLabel>
          </Grid>
          <TextInput
            label=""
            variant="outlined"
            fullWidth
            type="autocomplete"
            name="pathLab"
            value={formValues.pathLab}
            onChange={(val) => {
              const label = val?.label || val || '';
              handleChange({ target: { name: 'pathLab', value: label } });
              if (label) {
                saveClinicalDefault('laboratories', label).then(setLabOptions);
              }
            }}
            options={labOptions || []}
            onDelete={(option) => {
              deleteClinicalDefault('laboratories', option.label).then(
                setLabOptions
              );
            }}
            placeholder={t('Select or enter lab')}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="labReference"
          changed={changes.changed.labReference}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Lab Reference</FormLabel>
          </Grid>
          <TextInput
            label=""
            variant="outlined"
            fullWidth
            name="labReference"
            value={formValues.labReference}
            onChange={handleChange}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="pathReportDate"
          changed={changes.changed.pathReportDate}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Path Report Date</FormLabel>
          </Grid>
          <DateInput
            name="pathReportDate"
            value={asPickerDay(formValues.pathReportDate)}
            onChange={(val) =>
              handleChange({
                target: { name: 'pathReportDate', value: val, type: 'date' }
              })
            }
            disableFuture
            variant="outline"
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="ageAtRecord"
          changed={changes.changed.ageAtRecord}
          disabled={ageIsComputed}
        >
          <TextInput
            label="Age at Pathology Report"
            name="ageAtRecord"
            type="number"
            value={formValues.ageAtRecord ?? ''}
            inputProps={{ min: 0, className: 'no-spinners' }}
            // Disabled (muted) only when an age is actually being
            // auto-calculated, i.e. DoB AND pathology report date both set.
            // Editable when either is missing so a known age can be
            // recorded directly.
            disabled={!!patientDob && !!formValues.pathReportDate}
            onChange={
              patientDob && formValues.pathReportDate
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        ageAtRecord: val === '' ? '' : parsed
                      }));
                    }
                  }
            }
          />
        </ChangedField>

        <Grid size={12}>
          <Accordion
            expanded={showReportDates}
            onChange={(_, expanded) => setShowReportDates(expanded)}
            disableGutters
            elevation={0}
            sx={{ border: '1px solid #ccc', borderRadius: '5px !important' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <FormLabel>Record Report Request</FormLabel>
              {changedIn(['reportRequestDate', 'reportReceivedDate']) > 0 && (
                <ChangedSectionMarker
                  count={changedIn(['reportRequestDate', 'reportReceivedDate'])}
                  sx={{ ml: 1 }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <ChangedField
                  component={Grid}
                  size={6}
                  name="reportRequestDate"
                  changed={changes.changed.reportRequestDate}
                >
                  <Grid sx={{ paddingBottom: 1 }}>
                    <FormLabel>Report Request Date</FormLabel>
                  </Grid>
                  <DateInput
                    name="reportRequestDate"
                    value={formValues.reportRequestDate}
                    onChange={(val) =>
                      handleChange({
                        target: {
                          name: 'reportRequestDate',
                          value: val,
                          type: 'date'
                        }
                      })
                    }
                    disableFuture
                    variant="outline"
                  />
                </ChangedField>
                <ChangedField
                  component={Grid}
                  size={6}
                  name="reportReceivedDate"
                  changed={changes.changed.reportReceivedDate}
                >
                  <Grid sx={{ paddingBottom: 1 }}>
                    <FormLabel>Report Received Date</FormLabel>
                  </Grid>
                  <DateInput
                    name="reportReceivedDate"
                    value={formValues.reportReceivedDate}
                    onChange={(val) =>
                      handleChange({
                        target: {
                          name: 'reportReceivedDate',
                          value: val,
                          type: 'date'
                        }
                      })
                    }
                    variant="outline"
                  />
                </ChangedField>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid size={12}>
          <Accordion
            expanded={showBlockDates}
            onChange={(_, expanded) => setShowBlockDates(expanded)}
            disableGutters
            elevation={0}
            sx={{ border: '1px solid #ccc', borderRadius: '5px !important' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <FormLabel>Record Block/Tissue Request</FormLabel>
              {changedIn([
                'blocksRequestedDate',
                'blocksReceivedDate',
                'blocksReturnedDate'
              ]) > 0 && (
                <ChangedSectionMarker
                  count={changedIn([
                    'blocksRequestedDate',
                    'blocksReceivedDate',
                    'blocksReturnedDate'
                  ])}
                  sx={{ ml: 1 }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <ChangedField
                  component={Grid}
                  size={4}
                  name="blocksRequestedDate"
                  changed={changes.changed.blocksRequestedDate}
                >
                  <Grid sx={{ paddingBottom: 1 }}>
                    <FormLabel>Blocks Requested</FormLabel>
                  </Grid>
                  <DateInput
                    name="blocksRequestedDate"
                    value={formValues.blocksRequestedDate}
                    onChange={(val) =>
                      handleChange({
                        target: {
                          name: 'blocksRequestedDate',
                          value: val,
                          type: 'date'
                        }
                      })
                    }
                    variant="outline"
                  />
                </ChangedField>
                <ChangedField
                  component={Grid}
                  size={4}
                  name="blocksReceivedDate"
                  changed={changes.changed.blocksReceivedDate}
                >
                  <Grid sx={{ paddingBottom: 1 }}>
                    <FormLabel>Blocks Received</FormLabel>
                  </Grid>
                  <DateInput
                    name="blocksReceivedDate"
                    value={formValues.blocksReceivedDate}
                    onChange={(val) =>
                      handleChange({
                        target: {
                          name: 'blocksReceivedDate',
                          value: val,
                          type: 'date'
                        }
                      })
                    }
                    variant="outline"
                  />
                </ChangedField>
                <ChangedField
                  component={Grid}
                  size={4}
                  name="blocksReturnedDate"
                  changed={changes.changed.blocksReturnedDate}
                >
                  <Grid sx={{ paddingBottom: 1 }}>
                    <FormLabel>Blocks Returned</FormLabel>
                  </Grid>
                  <DateInput
                    name="blocksReturnedDate"
                    value={formValues.blocksReturnedDate}
                    onChange={(val) =>
                      handleChange({
                        target: {
                          name: 'blocksReturnedDate',
                          value: val,
                          type: 'date'
                        }
                      })
                    }
                    variant="outline"
                  />
                </ChangedField>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <ChangedField
          component={Grid}
          size={12}
          name="tissuePathComment"
          changed={changes.changed.tissuePathComment}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Tissue Path Comment</FormLabel>
          </Grid>
          <TextInput
            label=""
            variant="outlined"
            fullWidth
            multiline
            name="tissuePathComment"
            value={formValues.tissuePathComment}
            onChange={handleChange}
            placeholder={t('Add comments')}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          id="top"
          size={12}
          name="staffMember"
          changed={changes.changed.staffMember}
        >
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Staff Member</FormLabel>
          </Grid>
          <Grid size={12}>
            <TextInput
              label=""
              name="staffMember"
              type="multiple-select"
              value={formValues?.staffMember || []}
              onChange={handleChange}
              options={allStaffMembers}
              mapper={staffMembersMap || {}}
            />
          </Grid>
        </ChangedField>

        <ModalButtons
          isEditing={isEditing}
          hideContinueAdding={true}
          onClick={handleSubmit}
          handleClose={handleClose}
          addLabel="Save Tissue Pathology"
          intermediateBtnLabel="Delete"
          intermediateAction={isEditing ? () => setOpen(true) : null}
          unsavedCount={changes.count}
        />
      </Grid>
    </form>
  );
}
